import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { FiDownload, FiFileText } from 'react-icons/fi';
import {
  AnexoCirurgia,
  Apontamento,
  baixarAnexoApontamento,
  buscarApontamento,
} from '../../service/apontamentos';
import { dataHoraIsoParaBr, dataIsoParaBr } from '../../helpers/dataBr';
import { FILIAL_CFG } from '../../utils/getApontamentoColumn';

// CUIDADO com a escala gray.* aqui: o tema claro (src/styles/light-theme.css)
// INVERTE os tokens — gray.900 vira #ffffff (superfície) e gray.50 vira #1a1d26
// (texto principal). Então texto SEMPRE usa a ponta baixa da escala:
// gray.50 = texto principal, gray.400 = texto secundário. Usar gray.900 em texto
// pinta branco sobre branco e o dado desaparece no tema claro.
const Campo = ({ rotulo, valor }: { rotulo: string; valor?: string | null }) => (
  <Box minW={0}>
    <Text fontSize="10px" color="gray.400" fontWeight="700" letterSpacing="wider">
      {rotulo}
    </Text>
    <Text fontSize="sm" fontWeight="600" color="gray.50" noOfLines={2}>
      {valor || '—'}
    </Text>
  </Box>
);

// Rótulo das seções (MATERIAIS UTILIZADOS, ANEXOS).
const TituloSecao = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="11px" color="gray.400" fontWeight="700" letterSpacing="wider">
    {children}
  </Text>
);

interface Props {
  apontamento: Apontamento;
  onClose: () => void;
}

export const ApontamentoModal = ({ apontamento, onClose }: Props) => {
  const [detalhe, setDetalhe] = useState<Apontamento | null>(null);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [baixando, setBaixando] = useState('');

  const ehImagem = (anexo: AnexoCirurgia) => String(anexo.tipo_arquivo || '').startsWith('image/');

  // As imagens vêm por rota autenticada: são baixadas como blob e viram object
  // URLs, revogadas ao fechar para não segurar memória na aba.
  useEffect(() => {
    let ativo = true;
    const urlsCriadas: string[] = [];

    (async () => {
      try {
        const completo = await buscarApontamento(apontamento.apontamento_id);
        if (!ativo) { return; }
        setDetalhe(completo);

        const imagens = (completo.anexos || []).filter(ehImagem);
        const mapa: Record<string, string> = {};

        for (const anexo of imagens) {
          try {
            const blob = await baixarAnexoApontamento(
              apontamento.apontamento_id,
              anexo.cirurgia_anexo_id
            );
            if (!ativo) { return; }
            const url = URL.createObjectURL(blob);
            urlsCriadas.push(url);
            mapa[anexo.cirurgia_anexo_id] = url;
          } catch {
            // Miniatura ausente não impede o resto do detalhe; o botão de baixar continua.
          }
        }

        if (ativo) { setPreviews(mapa); }
      } catch (e: any) {
        if (ativo) { setErro(e?.message || 'Não foi possível abrir este apontamento.'); }
      } finally {
        if (ativo) { setCarregando(false); }
      }
    })();

    return () => {
      ativo = false;
      urlsCriadas.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [apontamento.apontamento_id]);

  const baixar = async (anexo: AnexoCirurgia) => {
    setBaixando(anexo.cirurgia_anexo_id);
    try {
      const blob = await baixarAnexoApontamento(apontamento.apontamento_id, anexo.cirurgia_anexo_id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = anexo.nome_original || `anexo-${anexo.ordem}`;
      link.click();
      // Revoga depois do clique: revogar na hora cancela o download em alguns navegadores.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      setErro('Não foi possível baixar este anexo.');
    } finally {
      setBaixando('');
    }
  };

  const filial = FILIAL_CFG[apontamento.filial];
  const hospital = apontamento.clientes?.fantasia || apontamento.clientes?.razao_social;

  // A coluna Qtde só entra quando algum material tem o dado — a maioria dos
  // apontamentos não preenche, e uma coluna inteira de "—" só rouba largura das
  // que importam (material, etiqueta, lote).
  const temQuantidade = (detalhe?.etiquetas || []).some((e) => e.quantidade != null);

  return (
    <Modal isOpen onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb="2">
          <Flex align="center" gap="10px" flexWrap="wrap">
            <Text fontSize="md" fontWeight="700" color="gray.50">
              {hospital || 'Apontamento'}
            </Text>
            <Badge colorScheme={filial?.cor || 'gray'} fontSize="10px">
              {filial?.label || apontamento.filial}
            </Badge>
            {!apontamento.agendamento_id && (
              <Badge colorScheme="orange" fontSize="10px">AVULSO</Badge>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb="6">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing="4" mb="4">
            <Campo rotulo="PACIENTE" valor={apontamento.paciente} />
            <Campo rotulo="MÉDICO" valor={apontamento.medico_nome} />
            <Campo rotulo="CONVÊNIO" valor={apontamento.convenio_nome} />
            <Campo rotulo="VENDEDOR" valor={apontamento.usuarios?.name} />
            <Campo
              rotulo="DATA DO PROCEDIMENTO"
              valor={dataIsoParaBr(String(apontamento.data_procedimento || '').split('T')[0])}
            />
            <Campo
              rotulo="APONTADO EM"
              valor={(() => {
                // Timestamp em UTC: converte pro fuso local para não errar hora/dia.
                const { data, hora } = dataHoraIsoParaBr(apontamento.data_cadastro);
                return data ? `${data} ${hora}` : '';
              })()}
            />
          </SimpleGrid>

          {/* Texto livre do vendedor para o faturamento. Vem do detalhe (e da
              listagem, se a API devolver lá também); sem texto, a seção some. */}
          {(detalhe?.observacoes || apontamento.observacoes) && (
            <Box mb="4">
              <TituloSecao>OBSERVAÇÕES</TituloSecao>
              <Text fontSize="sm" color="gray.50" whiteSpace="pre-wrap" wordBreak="break-word" mt="1">
                {detalhe?.observacoes || apontamento.observacoes}
              </Text>
            </Box>
          )}

          <Divider mb="4" />

          {carregando && (
            <Flex justify="center" py="8">
              <Spinner />
            </Flex>
          )}

          {!carregando && erro && (
            <Text color="red.500" fontSize="sm">{erro}</Text>
          )}

          {!carregando && detalhe && (
            <>
              <Box mb="2">
                <TituloSecao>MATERIAIS UTILIZADOS ({detalhe.etiquetas?.length || 0})</TituloSecao>
              </Box>

              {/* Tabela de verdade em vez de blocos empilhados: os materiais são
                  conferidos COLUNA a COLUNA contra a nota (todos os lotes, depois
                  todas as validades), e com blocos soltos o olho tinha de caçar
                  cada dado em posição diferente a cada linha. Colunas alinhadas
                  também eliminam o texto grudado ("Lote0001479829Validade..."):
                  cada dado vira uma célula, não textos irmãos dentro de um Flex. */}
              {!!detalhe.etiquetas?.length && (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th borderColor="gray.600" px="2" color="gray.400">Material</Th>
                        <Th borderColor="gray.600" px="2" color="gray.400">Etiqueta</Th>
                        <Th borderColor="gray.600" px="2" color="gray.400">Lote</Th>
                        <Th borderColor="gray.600" px="2" color="gray.400">Validade</Th>
                        {temQuantidade && (
                          <Th borderColor="gray.600" px="2" color="gray.400" isNumeric>Qtde</Th>
                        )}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(detalhe.etiquetas || []).map((etiqueta) => (
                        <Tr key={etiqueta.apontamento_etiqueta_id || etiqueta.codigo}>
                          <Td px="2" borderColor="gray.600">
                            <Text fontSize="13px" fontWeight="600" color="gray.50">
                              {etiqueta.produto_descricao || 'Produto não identificado'}
                            </Text>
                          </Td>
                          {/* Monoespaçada: o código é conferido dígito a dígito
                              contra a nota, e fonte de largura fixa alinha os
                              números verticalmente entre as linhas. */}
                          <Td px="2" borderColor="gray.600" whiteSpace="nowrap">
                            <Text fontFamily="mono" fontSize="13px" fontWeight="700" color="gray.50">
                              {etiqueta.codigo}
                            </Text>
                          </Td>
                          <Td px="2" borderColor="gray.600" whiteSpace="nowrap">
                            <Text fontFamily="mono" fontSize="12px" color="gray.50">
                              {etiqueta.lote || '—'}
                            </Text>
                          </Td>
                          <Td px="2" borderColor="gray.600" whiteSpace="nowrap">
                            <Text fontSize="12px" color="gray.50">
                              {etiqueta.validade
                                ? dataIsoParaBr(String(etiqueta.validade).split('T')[0])
                                : '—'}
                            </Text>
                          </Td>
                          {temQuantidade && (
                            <Td px="2" borderColor="gray.600" isNumeric>
                              <Text fontSize="12px" color="gray.50">
                                {etiqueta.quantidade ?? '—'}
                              </Text>
                            </Td>
                          )}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}

              {!detalhe.etiquetas?.length && (
                <Text fontSize="sm" color="gray.400">Nenhum material registrado.</Text>
              )}

              {!!detalhe.anexos?.length && (
                <>
                  <Box mt="6" mb="2">
                    <TituloSecao>ANEXOS ({detalhe.anexos.length})</TituloSecao>
                  </Box>

                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing="3">
                    {detalhe.anexos.map((anexo) => (
                      <Box
                        key={anexo.cirurgia_anexo_id}
                        borderWidth="1px"
                        borderColor="gray.600"
                        borderRadius="md"
                        overflow="hidden"
                      >
                        <Box h="120px" bg="gray.700">
                          {ehImagem(anexo) && previews[anexo.cirurgia_anexo_id] ? (
                            <Image
                              src={previews[anexo.cirurgia_anexo_id]}
                              alt={anexo.nome_original || 'Anexo'}
                              objectFit="cover"
                              w="100%"
                              h="100%"
                            />
                          ) : (
                            <Flex h="100%" align="center" justify="center" direction="column" gap="1">
                              <FiFileText size={26} color="#e53e3e" />
                              <Text fontSize="10px" color="gray.400" fontWeight="700">
                                {ehImagem(anexo) ? 'IMG' : 'PDF'}
                              </Text>
                            </Flex>
                          )}
                        </Box>

                        <Box p="2">
                          <Text fontSize="11px" color="gray.50" fontWeight="500" noOfLines={1}>
                            {anexo.nome_original || `Anexo ${anexo.ordem}`}
                          </Text>
                          <Badge
                            colorScheme={anexo.etapa === 'AGENDAMENTO' ? 'purple' : 'blue'}
                            fontSize="9px"
                            mt="1"
                          >
                            {anexo.etapa === 'AGENDAMENTO' ? 'AGENDAMENTO' : 'APONTAMENTO'}
                          </Badge>
                          <Button
                            size="xs"
                            variant="outline"
                            leftIcon={<FiDownload />}
                            w="100%"
                            mt="2"
                            isLoading={baixando === anexo.cirurgia_anexo_id}
                            onClick={() => baixar(anexo)}
                          >
                            Baixar
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </SimpleGrid>
                </>
              )}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
